const CHANNEL_NAME = 'saledesk-customer-display';
const STORAGE_KEY = 'saledesk-customer-display-state';

export type CustomerDisplayMessage = Record<string, unknown>;

let channel: BroadcastChannel | null = null;

function getChannel(): BroadcastChannel | null {
  if (typeof window === 'undefined' || !('BroadcastChannel' in window)) return null;
  if (!channel) channel = new BroadcastChannel(CHANNEL_NAME);
  return channel;
}

function persistSnapshot(payload: CustomerDisplayMessage) {
  if (payload.type !== 'cart' && payload.type !== 'thankyou') return;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* ignore quota / private mode */
  }
}

export function readPersistedCustomerDisplay(): CustomerDisplayMessage | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as CustomerDisplayMessage) : null;
  } catch {
    return null;
  }
}

/** POS → customer display (and Electron when available). */
export function publishCustomerDisplay(payload: CustomerDisplayMessage) {
  if (typeof window === 'undefined') return;

  const bridge = (window as unknown as { electronAPI?: { broadcastCustomerDisplay?: (p: unknown) => void } }).electronAPI;
  if (bridge?.broadcastCustomerDisplay) {
    bridge.broadcastCustomerDisplay(payload);
  }

  persistSnapshot(payload);
  getChannel()?.postMessage(payload);
}

/** Customer display (and POS for sync requests). */
export function subscribeCustomerDisplay(handler: (payload: CustomerDisplayMessage) => void) {
  const ch = getChannel();
  const onMessage = (event: MessageEvent) => {
    if (event.data && typeof event.data === 'object') handler(event.data as CustomerDisplayMessage);
  };
  ch?.addEventListener('message', onMessage);

  const persisted = readPersistedCustomerDisplay();
  if (persisted) handler(persisted);

  return () => ch?.removeEventListener('message', onMessage);
}

/** Ask the POS tab to re-send the current cart snapshot. */
export function requestCustomerDisplaySync() {
  getChannel()?.postMessage({ type: 'request-sync' });
}

const CUSTOMER_DISPLAY_WINDOW = 'saledesk-customer-display';
let customerDisplayWindow: Window | null = null;

/** Open (or focus) the customer-facing display on a second screen/window. */
export function openCustomerDisplayWindow(companyCode?: string | null) {
  if (typeof window === 'undefined') return null;

  const code = companyCode || localStorage.getItem('company_code');
  const params = code ? `?company=${encodeURIComponent(code)}` : '';
  const url = `${window.location.origin}/customer-display${params}`;

  if (customerDisplayWindow && !customerDisplayWindow.closed) {
    customerDisplayWindow.focus();
    requestCustomerDisplaySync();
    return customerDisplayWindow;
  }

  customerDisplayWindow = window.open(
    url,
    CUSTOMER_DISPLAY_WINDOW,
    'noopener,noreferrer,width=1024,height=768,menubar=no,toolbar=no,location=no,status=no',
  );
  if (customerDisplayWindow) {
    window.setTimeout(() => requestCustomerDisplaySync(), 500);
  }
  return customerDisplayWindow;
}

/** POS listens for sync requests from a newly opened display window. */
export function subscribeCustomerDisplaySyncRequests(onSync: () => void) {
  const ch = getChannel();
  const onMessage = (event: MessageEvent) => {
    if (event.data?.type === 'request-sync') onSync();
  };
  ch?.addEventListener('message', onMessage);
  return () => ch?.removeEventListener('message', onMessage);
}
